import { useTranslation } from 'next-i18next';

import { Button } from 'common/components/Button';
import { Input } from 'common/components/Form/Input';
import { Select } from 'common/components/Form/Select';
import { Textarea } from 'common/components/Form/Textarea';

import { useHook } from './hook';

interface Props {
  id: string;
  isProject?: boolean;
}

export const ContactForm: React.FC<Props> = ({ id, isProject }) => {
  const { t } = useTranslation('pageContact');
  const { fieldProps, formik, handleServiceClick, isSubmitting } = useHook({
    isProject: !!isProject,
  });

  const renderTextArea = () => (
    <Textarea
      className={isProject ? '' : 'mt-5'}
      required
      label={t('form.message.label')}
      name="message"
      errorMessage={formik.errors.message}
      {...fieldProps('message')}
    />
  );

  return (
    <section className="bg-gradient relative flex w-full items-center overflow-hidden pb-6 pt-20 md:pt-24 lg:pb-10 lg:pt-32">
      <form id={id} className="container" onSubmit={formik.handleSubmit}>
        <div className="mb-10 grid lg:grid-cols-2">
          <div>
            <h2
              className="mb-8 text-4xl font-semibold lg:text-5xl"
              dangerouslySetInnerHTML={{ __html: t('aboutYou') }}
            />
          </div>
          <div>
            <div className="m-0 hidden h-0 w-0 p-0">
              <Input
                className="mt-2"
                label={t('form.about.label')}
                name="about"
                required
                errorMessage={formik.errors.about}
                {...fieldProps('about')}
              />
            </div>
            <div>
              <Input
                className="mt-2"
                label={t('form.name.label')}
                name="name"
                required
                errorMessage={formik.errors.name}
                {...fieldProps('name')}
              />
            </div>
            <div className="grid gap-x-3 lg:grid-cols-2">
              <Input
                className="mt-2"
                label={t('form.email.label')}
                required
                name="email"
                errorMessage={formik.errors.email}
                {...fieldProps('email')}
              />
              <Input
                className="mt-2"
                type="tel"
                label={t('form.phoneNumber.label')}
                name="phoneNumber"
                errorMessage={formik.errors.phoneNumber}
                {...fieldProps('phoneNumber')}
              />
            </div>
            <div>
              <Input
                className="mt-2"
                label={t('form.companyName.label')}
                name="companyName"
                errorMessage={formik.errors.companyName}
                {...fieldProps('companyName')}
              />
            </div>

            <Input
              className="mt-5"
              label={t('form.howDidYouHearAboutUs.label')}
              name="howDidYouHearAboutUs"
              errorMessage={formik.errors.howDidYouHearAboutUs}
              {...fieldProps('howDidYouHearAboutUs')}
            />

            {!isProject && renderTextArea()}
          </div>
        </div>

        {!!isProject && (
          <>
            <div className="mb-10 grid lg:grid-cols-2">
              <div>
                <h2
                  className="mb-8 text-4xl font-semibold lg:text-5xl"
                  dangerouslySetInnerHTML={{ __html: t('aboutYourProject') }}
                />
              </div>

              <div className="grid gap-3">
                <Select
                  options={[
                    { label: t('buildANewDigitalProduct'), value: 'product' },
                    { label: t('outsourceTheDevelopmentOfExistingProject'), value: 'outsource' },
                    { label: t('notSureYet'), value: 'not-sure' },
                    { label: t('other'), value: 'other' },
                  ]}
                  required
                  label={'* ' + t('form.typeOfProject.label')}
                  errorMessage={(formik.errors as any).typeOfProject}
                  {...fieldProps('typeOfProject')}
                />

                <div className="mb-4 mt-4">
                  <div className="text-md pointer-events-none pl-0.5 font-normal text-white opacity-90 duration-300">
                    * {t('whatServicesDoYouNeed')}
                  </div>
                  <div className="mt-2 flex flex-row flex-wrap">
                    {[
                      { label: t('digitalStrategy'), value: 'digital-strategy' },
                      { label: t('uxUIDesign'), value: 'ux-ui-design' },
                      { label: t('content'), value: 'content' },
                      { label: t('website'), value: 'website' },
                      { label: t('webApplication'), value: 'web-application' },
                      { label: t('mobileApplication'), value: 'mobile-application' },
                      { label: t('digitalMarketing'), value: 'digital-marketing' },
                    ].map((item: { label: string; value: string }) => (
                      <div
                        key={item.value}
                        className="mr-0.5 mt-0.5 flex cursor-pointer items-center rounded-sm border-[1px] border-white/10 bg-[#261434] p-2 px-3 lg:p-3 lg:px-5"
                        onClick={handleServiceClick(item)}>
                        <div className="relative h-5 w-5 rounded-sm border-[1px] border-white/50">
                          {!!(formik.values as any).services?.some(
                            (x: { value: string }) => x.value === item.value,
                          ) && (
                            <div className="absolute bottom-1 left-1 right-1 top-1 bg-white/90" />
                          )}
                        </div>
                        <span className="mb-1 ml-2">{item.label}</span>
                      </div>
                    ))}
                  </div>
                  {!!isProject && !!(formik.errors as any).services && (
                    <div className="ml-2 mt-1.5 text-xs text-red-600">
                      {(formik.errors as any).services}
                    </div>
                  )}
                </div>

                {!!isProject && renderTextArea()}
              </div>
            </div>
          </>
        )}

        <div className="mb-10 grid lg:grid-cols-2">
          <div />
          <div className="flex justify-start">
            <Button className="w-full md:w-auto" button="submit" loading={isSubmitting}>
              {t('send')}
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
};
